import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Mail, Send } from "lucide-react";
import SEO from "../components/SEO";
import { supabase } from "../supabaseClient";

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [status, setStatus] = useState("idle"); // idle, submitting, success, error

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus("submitting");

        try {
            const { error } = await supabase
                .from("contact_messages")
                .insert([
                    { name: form.name, email: form.email, message: form.message }
                ]);

            if (error) throw error;

            setStatus("success");
            setTimeout(() => setStatus("idle"), 3000);
            setForm({ name: "", email: "", message: "" });
        } catch (error) {
            console.error("Error submitting contact form:", error);
            setStatus("error");
            setTimeout(() => setStatus("idle"), 4000);
        }
    };

    const contactDetails = [
        { icon: <MapPin className="w-5 h-5 text-blue-400" />, label: "Address", value: "Palam Triangle Block F 23A, Gurugram, Haryana 122017" },
        { icon: <Mail className="w-5 h-5 text-pink-400" />, label: "Email", value: "blinqbooks@gmail.com" },
    ];

    return (
        <div className="min-h-[85vh] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden bg-base transition-colors duration-300">
            <SEO
                title="Contact Us"
                description="Get in touch with the blinqbooks team. We're here to answer your questions and help you grow your business."
            />
            {/* Background Tech Elements */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute top-1/4 left-10 w-64 h-64 bg-blue-600/10 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-1/3 right-10 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl animate-pulse"></div>
            </div>

            <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 z-10 items-stretch">
                {/* Left - Contact Info Card */}
                <motion.div
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6 }}
                    className="relative h-full"
                >
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-purple-600/20 rounded-2xl opacity-50 blur-xl"></div>
                    <div className="card h-full flex flex-col p-6 sm:p-8 md:p-10 z-10 relative">
                        <div className="inline-block w-fit px-3 py-1 mb-6 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 text-sm font-medium">
                            👋 Get in Touch
                        </div>
                        <h1 className="text-3xl md:text-4xl font-bold mb-6 text-primary leading-tight">
                            Let's Talk About Your <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-cyan-500 to-emerald-500 dark:from-cyan-500 dark:via-blue-500 dark:to-emerald-500">
                                Business Growth
                            </span>
                        </h1>
                        <p className="text-secondary text-lg mb-12 leading-relaxed flex-grow">
                            Have questions about our AI suite? Our team is ready to help you optimize your workflow.
                        </p>

                        <div className="space-y-6 mt-auto">
                            {contactDetails.map((c, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.2 + (i * 0.1) }}
                                    className="flex items-center gap-4 p-4 rounded-xl hover:bg-slate-500/[0.04] dark:hover:bg-white/[0.02] transition-colors group cursor-default"
                                >
                                    <div className="p-3 rounded-lg bg-slate-500/10 dark:bg-white/5 group-hover:scale-110 transition-transform">
                                        {c.icon}
                                    </div>
                                    <div>
                                        <p className="text-sm text-secondary">{c.label}</p>
                                        <p className="font-medium text-primary">{c.value}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </motion.div>

                {/* Right - Form Card */}
                <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="relative h-full"
                >
                    <div className="absolute inset-0 bg-gradient-to-bl from-blue-500 to-purple-500 rounded-2xl opacity-20 blur-xl"></div>
                    <div className="card h-full flex flex-col justify-center p-6 sm:p-8 md:p-10 relative z-10">
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-primary mb-2">Send us a Message</h2>
                            <p className="text-secondary text-sm">We usually respond within 24 hours.</p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-secondary">Name</label>
                                    <input
                                        type="text"
                                        placeholder="John Doe"
                                        className="w-full px-4 py-3 rounded-lg bg-slate-500/5 dark:bg-white/5 border border-slate-200 dark:border-slate-800 text-primary placeholder-tertiary focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent/50 transition-all"
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-secondary">Email</label>
                                    <input
                                        type="email"
                                        placeholder="john@work.com"
                                        className="w-full px-4 py-3 rounded-lg bg-slate-500/5 dark:bg-white/5 border border-slate-200 dark:border-slate-800 text-primary placeholder-tertiary focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent/50 transition-all"
                                        value={form.email}
                                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-secondary">Message</label>
                                <textarea
                                    placeholder="Tell us about your project..."
                                    rows="4"
                                    className="w-full px-4 py-3 rounded-lg bg-slate-500/5 dark:bg-white/5 border border-slate-200 dark:border-slate-800 text-primary placeholder-tertiary focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent/50 transition-all resize-none"
                                    value={form.message}
                                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                                    required
                                />
                            </div>

                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                type="submit"
                                disabled={status === "submitting" || status === "success"}
                                className={`btn-primary w-full flex items-center justify-center gap-2 ${status === "success" ? "!bg-green-500 hover:!bg-green-600" : status === "error" ? "!bg-red-500 hover:!bg-red-600" : ""}`}
                            >
                                {status === "submitting" ? (
                                    <span className="flex items-center gap-2">
                                        <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Sending...
                                    </span>
                                ) : status === "success" ? (
                                    <span className="flex items-center gap-2">
                                        Message Sent! 🎉
                                    </span>
                                ) : status === "error" ? (
                                    <span className="flex items-center gap-2">
                                        Error Sending! ❌
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-2">
                                        Send Message <Send className="w-4 h-4" />
                                    </span>
                                )}
                            </motion.button>
                        </form>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
